
import { useState, useEffect, useCallback } from 'react';
import styles from './ProductGallery.module.css';

interface ProductGalleryProps {
    images: Array<{
        id: number;
        src: string;
        fullSrc?: string;
        alt: string;
    }>;
    imagePositions?: Record<string | number, string>;
}

export const ProductGallery = ({ images, imagePositions = {} }: ProductGalleryProps) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
    const [zoomed, setZoomed] = useState(false);

    const goToSlide = (index: number) => {
        setCurrentIndex(index);
    };

    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // Minimum swipe distance (in px)
    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            if (currentIndex < images.length - 1) {
                setCurrentIndex(prev => prev + 1);
            }
        }

        if (isRightSwipe) {
            if (currentIndex > 0) {
                setCurrentIndex(prev => prev - 1);
            }
        }
    };

    const openLightbox = (index: number) => {
        setLightboxIndex(index);
        setZoomed(false);
    };

    const closeLightbox = () => {
        setLightboxIndex(null);
        setZoomed(false);
    };

    const goToPrev = useCallback(() => {
        if (lightboxIndex !== null && lightboxIndex > 0) {
            setLightboxIndex(lightboxIndex - 1);
            setZoomed(false);
        }
    }, [lightboxIndex]);

    const goToNext = useCallback(() => {
        if (lightboxIndex !== null && lightboxIndex < images.length - 1) {
            setLightboxIndex(lightboxIndex + 1);
            setZoomed(false);
        }
    }, [lightboxIndex, images.length]);

    useEffect(() => {
        if (lightboxIndex === null) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') goToPrev();
            if (e.key === 'ArrowRight') goToNext();
        };

        document.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [lightboxIndex, goToPrev, goToNext]);

    return (
        <div
            className={styles.galleryContainer}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
        >
            <div
                className={styles.slider}
                style={{ '--current-slide': currentIndex } as React.CSSProperties}
            >
                {images.map((image, index) => (
                    <div key={image.id} className={styles.slide} onClick={() => openLightbox(index)}>
                        <img
                            src={image.src}
                            alt={image.alt}
                            className={styles.image}
                            style={{ objectPosition: imagePositions[image.id] || 'center' }}
                            loading="lazy"
                        />
                    </div>
                ))}
            </div>

            <div className={styles.dotsContainer}>
                {images.map((_, index) => (
                    <button
                        key={index}
                        className={`${styles.dot} ${index === currentIndex ? styles.activeDot : ''}`}
                        onClick={() => goToSlide(index)}
                        aria-label={`Go to slide ${index + 1}`}
                    />
                ))}
            </div>

            {lightboxIndex !== null && (
                <div className={styles.lightboxOverlay} onClick={closeLightbox}>
                    <button className={styles.lightboxClose} onClick={closeLightbox} aria-label="Fermer">
                        &times;
                    </button>

                    {lightboxIndex > 0 && (
                        <button
                            className={`${styles.lightboxNav} ${styles.lightboxNavPrev}`}
                            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                            aria-label="Image precedente"
                        >
                            &#8249;
                        </button>
                    )}

                    <div
                        className={`${styles.lightboxContent} ${zoomed ? styles.lightboxContentZoomed : ''}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img
                            src={images[lightboxIndex].fullSrc || images[lightboxIndex].src}
                            alt={images[lightboxIndex].alt}
                            className={`${styles.lightboxImage} ${zoomed ? styles.lightboxImageZoomed : ''}`}
                            onClick={() => setZoomed(!zoomed)}
                            draggable={false}
                        />
                    </div>

                    {lightboxIndex < images.length - 1 && (
                        <button
                            className={`${styles.lightboxNav} ${styles.lightboxNavNext}`}
                            onClick={(e) => { e.stopPropagation(); goToNext(); }}
                            aria-label="Image suivante"
                        >
                            &#8250;
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
